import { supabase, supabaseUrl, supabaseAnonKey } from './supabase'
import { invokeEdgeFunction } from './supabaseEdge'

export async function fetchAgentPermissions() {
  const res = await invokeEdgeFunction('kb-agent-permissions', { method: 'GET' })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.message ?? `Permissions failed (${res.status})`)
  return body
}

export async function listAgentConversations() {
  const res = await invokeEdgeFunction('kb-agent-chat?list=1', { method: 'GET' })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.message ?? `List failed (${res.status})`)
  return body.conversations ?? []
}

export async function loadAgentConversation(conversationId) {
  const res = await invokeEdgeFunction(
    `kb-agent-chat?conversation_id=${encodeURIComponent(conversationId)}`,
    { method: 'GET' }
  )
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.message ?? `Load failed (${res.status})`)
  return body
}

export async function createAgentConversation({ clientId, includePersonal } = {}) {
  const res = await invokeEdgeFunction('kb-agent-chat', {
    method: 'POST',
    body: JSON.stringify({
      action: 'new_conversation',
      client_id: clientId || undefined,
      include_personal: includePersonal === true,
    }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.message ?? `Create failed (${res.status})`)
  return body.conversation
}

export async function renameAgentConversation(conversationId, title) {
  const res = await invokeEdgeFunction('kb-agent-chat', {
    method: 'POST',
    body: JSON.stringify({
      action: 'rename_conversation',
      conversation_id: conversationId,
      title: title.trim(),
    }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.message ?? `Rename failed (${res.status})`)
  return body.conversation
}

export async function deleteAgentConversation(conversationId) {
  const res = await invokeEdgeFunction('kb-agent-chat', {
    method: 'POST',
    body: JSON.stringify({ action: 'delete_conversation', conversation_id: conversationId }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.message ?? `Delete failed (${res.status})`)
  return body
}

export async function sendAgentMessage({
  message,
  conversationId,
  clientId,
  includePersonal,
}) {
  const res = await invokeEdgeFunction('kb-agent-chat', {
    method: 'POST',
    body: JSON.stringify({
      action: 'send',
      message,
      conversation_id: conversationId || undefined,
      client_id: clientId || undefined,
      include_personal: includePersonal === true,
      stream: false,
    }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(body.message ?? `Agent failed (${res.status})`)
    err.hintCode = body.hint_code
    throw err
  }
  return body
}

/** Parse SSE buffer into events; returns unconsumed remainder. */
function parseSseBuffer(buffer) {
  const normalized = buffer.replace(/\r\n/g, '\n')
  const parts = normalized.split('\n\n')
  const remainder = parts.pop() ?? ''
  const events = []

  for (const block of parts) {
    const dataLines = block
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trim())
    if (!dataLines.length) continue
    const jsonStr = dataLines.join('\n')
    try {
      events.push(JSON.parse(jsonStr))
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[kbAgent] SSE parse failed:', jsonStr.slice(0, 120), e)
      }
    }
  }

  return { events, remainder }
}

function dispatchSseEvents(events, onEvent) {
  for (const data of events) {
    onEvent(data)
  }
}

/**
 * Stream agent turn via SSE. Calls onEvent for each server event.
 * Event types: status, tool_selected, tool_start, tool_done, content_start, content_delta, message, error, title.
 * @returns {{ aborted: boolean }}
 */
export async function sendAgentMessageStream(
  { message, conversationId, clientId, includePersonal, signal },
  onEvent
) {
  if (!supabaseUrl || !supabaseAnonKey) throw new Error('Supabase URL / anon key missing')
  const session = (await supabase.auth.getSession()).data.session
  const token = session?.access_token
  if (!token) throw new Error('Not authenticated')

  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/kb-agent-chat`
  const res = await fetch(url, {
    method: 'POST',
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: supabaseAnonKey,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      action: 'send',
      message,
      conversation_id: conversationId || undefined,
      client_id: clientId || undefined,
      include_personal: includePersonal === true,
      stream: true,
    }),
  })

  if (signal?.aborted) return { aborted: true }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const err = new Error(body.message ?? `Agent failed (${res.status})`)
    err.hintCode = body.hint_code
    throw err
  }

  const contentType = res.headers.get('Content-Type') || ''
  if (!contentType.includes('text/event-stream')) {
    const body = await res.json().catch(() => ({}))
    if (body.message) {
      onEvent({
        type: 'message',
        conversation_id: body.conversation_id,
        message: body.message,
      })
    }
    if (body.title && body.conversation_id) {
      onEvent({ type: 'title', title: body.title, conversation_id: body.conversation_id })
    }
    return { aborted: false }
  }

  const reader = res.body?.getReader()
  if (!reader) throw new Error('No response stream')

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (signal?.aborted) {
        await reader.cancel().catch(() => {})
        return { aborted: true }
      }
      buffer += decoder.decode(value, { stream: true })
      const { events, remainder } = parseSseBuffer(buffer)
      buffer = remainder
      dispatchSseEvents(events, onEvent)
    }

    buffer += decoder.decode()
    if (buffer.trim()) {
      const { events } = parseSseBuffer(`${buffer}\n\n`)
      dispatchSseEvents(events, onEvent)
    }
  } catch (e) {
    if (signal?.aborted || e?.name === 'AbortError') return { aborted: true }
    throw e
  }

  return { aborted: false }
}

export const PHASE_LABELS = {
  thinking: 'Thinking…',
  planning: 'Choosing the best approach…',
  analysing: 'Preparing your answer…',
  calling_model: 'Calling model…',
}

function truncateText(text, max = 60) {
  const s = String(text || '').trim()
  if (!s) return ''
  if (s.length <= max) return s
  return `${s.slice(0, max - 1)}…`
}

export function reviewingRequestLabel(userText) {
  const snippet = truncateText(userText, 60)
  return snippet ? `Reviewing your request: "${snippet}"` : 'Reviewing your request…'
}

export function friendlyToolName(toolName) {
  const map = {
    search_knowledge: 'document search',
    get_document: 'document reader',
    analyze_evidence: 'evidence analysis',
    knowledge_query: 'knowledge query',
    wiki_search: 'wiki search',
    wiki_read_page: 'wiki page',
    wiki_read_index: 'wiki index',
    wiki_list_pages: 'wiki pages',
    wiki_graph: 'wiki graph',
    wiki_business_graph: 'business graph',
    list_sources: 'source list',
  }
  return map[toolName] || String(toolName || '').replace(/_/g, ' ')
}

export function askingModelLabel(model, provider) {
  const name = String(model || provider || '').trim()
  return name ? `Asking ${name} which tools to use…` : 'Asking the model which tools to use…'
}

export function returningToModelLabel(model, provider) {
  const name = String(model || provider || '').trim()
  return name ? `Sending results back to ${name}…` : 'Sending results back to the model…'
}

export function selectedToolLabel(tool, query) {
  const name = friendlyToolName(tool)
  const q = String(query || '').trim()
  if (q) return `Selected ${name} for "${truncateText(q, 80)}"`
  return `Selected ${name}`
}

export function runningToolLabel(tool, query) {
  const base = toolStatusLabel(tool)
  const q = String(query || '').trim()
  if (!q) return base
  const trimmed = base.replace(/…$/, '')
  return `${trimmed} "${truncateText(q, 80)}"…`
}

export function initAgentActivity(userText) {
  const ctx = {
    userText: String(userText || '').trim(),
    model: '',
    provider: '',
    sawTools: false,
    iteration: 0,
    lastSeq: 0,
  }
  return {
    ctx,
    activity: {
      id: 'reviewing',
      label: reviewingRequestLabel(ctx.userText),
      kind: 'reviewing',
      status: 'active',
    },
  }
}

/**
 * Map one SSE event → next activity line. Returns activity: undefined to keep current line.
 */
export function nextAgentActivity(prevCtx, ev) {
  const ctx = { ...(prevCtx || {}) }

  if (ev?.seq != null && ev.seq <= (ctx.lastSeq ?? 0)) {
    return { ctx, activity: undefined }
  }
  if (ev?.seq != null) ctx.lastSeq = ev.seq

  if (ev?.type === 'tool_done') {
    return { ctx, activity: undefined }
  }

  if (ev?.type === 'content_start') {
    return { ctx, activity: null }
  }

  if (ev?.type === 'error') {
    return {
      ctx,
      activity: {
        id: `error-${ev.seq ?? Date.now()}`,
        label: String(ev.message || 'Something went wrong'),
        kind: 'error',
        status: 'error',
      },
    }
  }

  if (ev?.type === 'status' && ev.phase === 'planning') {
    return {
      ctx,
      activity: {
        id: 'planning',
        label: PHASE_LABELS.planning,
        kind: 'planning',
        status: 'active',
      },
    }
  }

  if (ev?.type === 'status' && ev.phase === 'calling_model') {
    const iteration = Number(ev.iteration) || ctx.iteration || 1
    ctx.iteration = iteration
    if (ev.model) ctx.model = String(ev.model)
    if (ev.provider) ctx.provider = String(ev.provider)
    const label =
      iteration > 1
        ? returningToModelLabel(ctx.model, ctx.provider)
        : askingModelLabel(ctx.model, ctx.provider)
    return {
      ctx,
      activity: {
        id: `calling-${iteration}`,
        label,
        kind: 'calling_model',
        status: 'active',
      },
    }
  }

  if (ev?.type === 'status' && ev.phase === 'analysing') {
    return {
      ctx,
      activity: {
        id: 'analysing',
        label: PHASE_LABELS.analysing,
        kind: 'analysing',
        status: 'active',
      },
    }
  }

  if (ev?.type === 'status' && ev.phase === 'thinking') {
    return {
      ctx,
      activity: {
        id: 'planning',
        label: PHASE_LABELS.planning,
        kind: 'planning',
        status: 'active',
      },
    }
  }

  if (ev?.type === 'tool_selected' && ev.tool) {
    ctx.sawTools = true
    return {
      ctx,
      activity: {
        id: `selected-${ev.tool}-${ev.seq ?? ''}`,
        label: selectedToolLabel(ev.tool, ev.query),
        kind: 'tool_selected',
        status: 'active',
      },
    }
  }

  if (ev?.type === 'tool_start' && ev.tool) {
    ctx.sawTools = true
    return {
      ctx,
      activity: {
        id: `tool-${ev.tool}-${ev.seq ?? ''}`,
        label: runningToolLabel(ev.tool, ev.query),
        kind: 'tool_running',
        status: 'active',
      },
    }
  }

  return { ctx, activity: undefined }
}

export function callingModelLabel(model, provider) {
  const name = String(model || '').trim()
  if (name) return `Calling ${name}…`
  const p = String(provider || '').trim()
  if (p) return `Calling ${p}…`
  return PHASE_LABELS.calling_model
}

export const TOOL_STATUS_LABELS = {
  wiki_search: 'Searching wiki…',
  wiki_read_page: 'Reading wiki page…',
  wiki_read_index: 'Reading wiki index…',
  wiki_list_pages: 'Listing wiki pages…',
  wiki_graph: 'Loading wiki graph…',
  wiki_business_graph: 'Loading business graph…',
  search_knowledge: 'Searching documents…',
  get_document: 'Reading document…',
  analyze_evidence: 'Analyzing evidence…',
  knowledge_query: 'Querying knowledge…',
  list_sources: 'Listing sources…',
}

export function toolStatusLabel(toolName) {
  return TOOL_STATUS_LABELS[toolName] || `Running ${toolName}…`
}

export function phaseStatusLabel(phase) {
  return PHASE_LABELS[phase] || phase
}
